import { useContext } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { AuthSessionContext } from '@context/AuthContext'
import { useEntitySettings } from '@context/EntityContext'
import { useNotificationsContext } from '@context/NotificationsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { entityPerTickmillCompany, getTickmillCompanyByHostname } from '@utils/companyName.utils'
import { dispatchEntitySelected } from '@utils/cookie.utils'
import { loginToAuth } from '@utils/loginToAuth'
import { TokenStorage } from '@utils/tokenStorage'

import { AuthUser, LoginResponse } from '@model/User'

import { safeLocalStorage, useLocallyPersistedState } from '@hooks/useStorage'

export interface SignInAuthResult {
  auth: AuthUser | null
  shouldContinueNavigation: boolean
}

export const useSignInAuth = (): {
  handleSignInAuth: (
    authData: LoginResponse | AuthUser,
    isUsersAuthAlreadyValidated?: boolean
  ) => Promise<SignInAuthResult>
} => {
  const [, setNotificationSettings] = useNotificationsContext()
  const [, setLuckyDrawViewed] = useLocallyPersistedState('luckyDrawViewed', false)
  const [, setAuth] = useContext(AuthSessionContext)
  const [, setEntity] = useEntitySettings()
  const locale = useSessionLanguage()
  const navigate = useNavigate()

  const handleSignInAuth = async (
    authData: LoginResponse | AuthUser,
    isUsersAuthPreValidated?: boolean
  ): Promise<SignInAuthResult> => {
    setLuckyDrawViewed(false)
    setNotificationSettings({
      hideGeneralNotifications: false,
      hidePromoNotifications: false,
      hidePlatformNotifications: false,
    })

    const auth = isUsersAuthPreValidated
      ? (authData as AuthUser)
      : loginToAuth(authData as LoginResponse)

    if (!auth) {
      return { auth: null, shouldContinueNavigation: false }
    }

    setAuth(auth)
    if (
      auth.tickmillCompany &&
      window.env?.APP_ENTITY?.length &&
      !window?.env?.APP_ENTITY.includes(entityPerTickmillCompany[auth.tickmillCompany.id]) &&
      process.env.NODE_ENV !== 'development'
    ) {
      navigate('/wrong-entity')
      return { auth, shouldContinueNavigation: false }
    } else {
      safeLocalStorage.setJSON('auth', auth)
      if (auth.accessToken) {
        TokenStorage.storeToken('access', auth.accessToken)
      }
      if (auth.refreshToken) {
        TokenStorage.storeToken('refresh', auth.refreshToken)
      }
      if (auth.sessionId) {
        TokenStorage.storeSession(auth.sessionId)
      }
      if (auth.tickmillCompany) {
        setEntity({ entity: auth.tickmillCompany.id })
      }
      if (auth.email && auth.firstName) {
        dispatchEntitySelected(
          auth?.tickmillCompany.id || getTickmillCompanyByHostname(),
          locale,
          auth.email,
          auth.firstName
        )
      }
    }

    return { auth, shouldContinueNavigation: true }
  }

  return { handleSignInAuth }
}
